import { appConfirm } from '../../services/appDialogs';
import { AppSelect } from '../ui/AppSelect';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Film,
  ImagePlus,
  Loader2,
  MessageSquareWarning,
  Pause,
  Play,
  Send,
  SkipBack,
  SkipForward,
  Trash2,
  X,
} from "lucide-react";
import { authenticatedFetch } from "../../utils/api";
import { API_HOST } from "../../utils/constants";
import { uploadFileToCos } from "../../utils/fileUtils";
import { useToast } from "../../context/ToastContext";
import "./EpisodeReviewFeedbackPage.css";

const PRIORITIES = [
  { id: "P0", name: "P0 必修", color: "var(--color-error)" },
  { id: "P1", name: "P1 重要", color: "var(--color-accent-secondary)" },
  { id: "P2", name: "P2 普通", color: "var(--color-warning)" },
  { id: "P3", name: "P3 建议", color: "var(--color-success)" },
];

const ISSUE_TYPES = [
  { id: "story", name: "剧情缺失" },
  { id: "continuity", name: "连续性" },
  { id: "character", name: "人物/脸" },
  { id: "action", name: "动作调度" },
  { id: "vfx", name: "特效" },
  { id: "shot", name: "镜头" },
  { id: "sound", name: "声音" },
  { id: "other", name: "其他" },
];

const ISSUE_STATUSES = [
  { id: "open", name: "待处理" },
  { id: "doing", name: "处理中" },
  { id: "resolved", name: "已处理" },
  { id: "ignored", name: "暂不处理" },
];

const QUICK_STATUSES = [
  { id: "resolved", name: "已处理" },
  { id: "doing", name: "处理中" },
  { id: "ignored", name: "暂不处理" },
];

const CLOSED_STATUSES = new Set(["resolved", "ignored"]);
const TIMELINE_MIN_SEGMENT_WIDTH = 142;
const TIMELINE_PX_PER_SECOND = 15;
const TIMELINE_GAP = 6;

const firstText = (...values) => {
  for (const value of values) {
    if (value != null && String(value).trim()) return String(value).trim();
  }
  return "";
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, Number(value) || 0));

const formatTime = (value, precise = false) => {
  const seconds = Math.max(0, Number(value) || 0);
  const minutes = Math.floor(seconds / 60);
  const rest = seconds - minutes * 60;
  const formattedSeconds = precise ? rest.toFixed(1).padStart(4, "0") : String(Math.floor(rest)).padStart(2, "0");
  return `${String(minutes).padStart(2, "0")}:${formattedSeconds}`;
};

const getPriority = (value) => (
  PRIORITIES.find((item) => item.id === firstText(value).toUpperCase()) || PRIORITIES[2]
);

const getTypeName = (value) => ISSUE_TYPES.find((item) => item.id === value)?.name || "其他";
const getStatusName = (value) => ISSUE_STATUSES.find((item) => item.id === value)?.name || "待处理";

const createDraft = ({ projectId, episodeId, seconds = 0, segmentIndex = 1 }) => ({
  projectId: Number(projectId),
  episodeId: Number(episodeId),
  segmentIndex,
  timecodeSeconds: Math.max(0, Math.round((Number(seconds) || 0) * 10) / 10),
  priority: "P2",
  type: "story",
  status: "open",
  title: "",
  problem: "",
  reason: "",
  suggestion: "",
  referenceImages: [],
});

const buildTimeline = (segments = []) => {
  let secondsCursor = 0;
  let pixelCursor = 0;
  const rows = [...segments]
    .sort((a, b) => Number(a.segmentIndex || 0) - Number(b.segmentIndex || 0))
    .map((segment, arrayIndex) => {
      const durationSeconds = clamp(segment?.durationSeconds || 15, 1, 15);
      const width = Math.max(TIMELINE_MIN_SEGMENT_WIDTH, durationSeconds * TIMELINE_PX_PER_SECOND);
      const row = {
        ...segment,
        arrayIndex,
        durationSeconds,
        startSeconds: secondsCursor,
        endSeconds: secondsCursor + durationSeconds,
        left: pixelCursor,
        right: pixelCursor + width,
        width,
      };
      secondsCursor += durationSeconds;
      pixelCursor += width + TIMELINE_GAP;
      return row;
    });
  return {
    rows,
    totalDuration: secondsCursor,
    width: Math.max(0, pixelCursor - TIMELINE_GAP),
  };
};

const EpisodeReviewFeedbackPage = () => {
  const { projectId, episodeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const videoRef = useRef(null);
  const nextVideoRef = useRef(null);
  const timelineRef = useRef(null);
  const uploadInputRef = useRef(null);
  const pendingSeekRef = useRef(null);
  const initialLocationAppliedRef = useRef(false);
  const dragCleanupRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [episode, setEpisode] = useState(null);
  const [segments, setSegments] = useState([]);
  const [issues, setIssues] = useState([]);
  const [activeSegmentIndex, setActiveSegmentIndex] = useState(0);
  const [playheadSeconds, setPlayheadSeconds] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [draft, setDraft] = useState(() => createDraft({ projectId, episodeId }));
  const [focusedIssueId, setFocusedIssueId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("active");

  const timeline = useMemo(() => buildTimeline(segments), [segments]);
  const activeSegment = timeline.rows[activeSegmentIndex] || null;
  const nextPlayableSegment = useMemo(
    () => timeline.rows.slice(activeSegmentIndex + 1).find((item) => firstText(item.videoUrl)),
    [activeSegmentIndex, timeline.rows],
  );

  const currentSegmentAt = useCallback((seconds) => {
    if (!timeline.rows.length) return null;
    const bounded = clamp(seconds, 0, timeline.totalDuration);
    return timeline.rows.find((item, index) => (
      bounded >= item.startSeconds
      && (bounded < item.endSeconds || index === timeline.rows.length - 1)
    )) || timeline.rows[timeline.rows.length - 1];
  }, [timeline.rows, timeline.totalDuration]);

  const pixelAtSeconds = useCallback((seconds) => {
    const segment = currentSegmentAt(seconds);
    if (!segment) return 0;
    const ratio = clamp(
      (Number(seconds) - segment.startSeconds) / Math.max(1, segment.durationSeconds),
      0,
      1,
    );
    return segment.left + segment.width * ratio;
  }, [currentSegmentAt]);

  const secondsAtPixel = useCallback((pixel) => {
    if (!timeline.rows.length) return 0;
    const bounded = clamp(pixel, 0, timeline.width);
    const segment = timeline.rows.find((item) => bounded <= item.right)
      || timeline.rows[timeline.rows.length - 1];
    if (bounded < segment.left) return segment.startSeconds;
    const ratio = clamp((bounded - segment.left) / Math.max(1, segment.width), 0, 1);
    return segment.startSeconds + segment.durationSeconds * ratio;
  }, [timeline.rows, timeline.width]);

  const filteredIssues = useMemo(() => issues.filter((issue) => {
    const statusMatches = filterStatus === "all"
      || (filterStatus === "active" && !CLOSED_STATUSES.has(firstText(issue.status).toLowerCase()))
      || firstText(issue.status).toLowerCase() === filterStatus;
    return statusMatches
      && (filterPriority === "all" || issue.priority === filterPriority)
      && (filterType === "all" || issue.type === filterType);
  }), [filterPriority, filterStatus, filterType, issues]);

  const openIssueCount = useMemo(
    () => issues.filter((item) => !CLOSED_STATUSES.has(firstText(item.status).toLowerCase())).length,
    [issues],
  );

  const loadPageData = useCallback(async () => {
    if (!projectId || !episodeId) return;
    setLoading(true);
    setError("");
    try {
      const [episodeList, storyboard, issueResult] = await Promise.all([
        authenticatedFetch(`${API_HOST}/api/project/episode/list?projectId=${projectId}`),
        authenticatedFetch(`${API_HOST}/api/episode-mini-storyboard/list?projectId=${projectId}&episodeId=${episodeId}`),
        authenticatedFetch(`${API_HOST}/api/episode-revision-issues?projectId=${projectId}&episodeId=${episodeId}`),
      ]);
      const episodeRows = Array.isArray(episodeList) ? episodeList : [];
      setEpisode(episodeRows.find((item) => String(item.id) === String(episodeId)) || null);
      setSegments(Array.isArray(storyboard?.segments) ? storyboard.segments : []);
      setIssues(Array.isArray(issueResult?.issues) ? issueResult.issues : []);
    } catch (requestError) {
      const message = requestError?.message || "审片数据加载失败";
      setError(message);
      toast.error(message, 0);
    } finally {
      setLoading(false);
    }
  }, [episodeId, projectId, toast]);

  useEffect(() => {
    void loadPageData();
  }, [loadPageData]);

  const seekTo = useCallback((requestedSeconds, options = {}) => {
    if (!timeline.rows.length) return;
    const seconds = clamp(requestedSeconds, 0, timeline.totalDuration);
    let target = currentSegmentAt(seconds);
    if (!target) return;
    if (!firstText(target.videoUrl)) {
      target = timeline.rows
        .filter((item) => firstText(item.videoUrl))
        .sort((a, b) => (
          Math.abs(a.startSeconds - seconds) - Math.abs(b.startSeconds - seconds)
        ))[0] || target;
    }
    const targetArrayIndex = target.arrayIndex;
    const targetSeconds = firstText(target.videoUrl) ? clamp(seconds, target.startSeconds, target.endSeconds) : target.startSeconds;
    const shouldPlay = Boolean(options.autoPlay);
    setPlayheadSeconds(targetSeconds);
    if (targetArrayIndex !== activeSegmentIndex) {
      pendingSeekRef.current = {
        arrayIndex: targetArrayIndex,
        localSeconds: clamp(targetSeconds - target.startSeconds, 0, target.durationSeconds),
        autoPlay: shouldPlay,
      };
      setActiveSegmentIndex(targetArrayIndex);
      setPlaying(shouldPlay);
      return;
    }
    const video = videoRef.current;
    if (video && firstText(target.videoUrl)) {
      video.currentTime = clamp(targetSeconds - target.startSeconds, 0, Math.max(0, video.duration || target.durationSeconds));
      if (shouldPlay) {
        video.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
      } else {
        video.pause();
        setPlaying(false);
      }
    }
  }, [activeSegmentIndex, currentSegmentAt, timeline.rows, timeline.totalDuration]);

  useEffect(() => {
    if (loading || !timeline.rows.length || initialLocationAppliedRef.current) return;
    initialLocationAppliedRef.current = true;
    const requestedTime = Number(location.state?.timecodeSeconds);
    const requestedIssueId = Number(location.state?.issueId);
    if (Number.isFinite(requestedTime)) seekTo(requestedTime, { autoPlay: false });
    if (requestedIssueId) {
      const selected = issues.find((item) => Number(item.id) === requestedIssueId);
      if (selected) setFocusedIssueId(selected.id);
    }
  }, [issues, loading, location.state, seekTo, timeline.rows.length]);

  useEffect(() => () => {
    dragCleanupRef.current?.();
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    const pending = pendingSeekRef.current;
    const video = videoRef.current;
    if (!video || !activeSegment) return;
    if (pending && pending.arrayIndex === activeSegmentIndex) {
      video.currentTime = clamp(pending.localSeconds, 0, Math.max(0, video.duration || activeSegment.durationSeconds));
      pendingSeekRef.current = null;
      if (pending.autoPlay) {
        video.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
      }
    }
  }, [activeSegment, activeSegmentIndex]);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video || !activeSegment) return;
    setPlayheadSeconds(clamp(
      activeSegment.startSeconds + (Number(video.currentTime) || 0),
      activeSegment.startSeconds,
      activeSegment.endSeconds,
    ));
  }, [activeSegment]);

  const playNext = useCallback(() => {
    const next = timeline.rows.slice(activeSegmentIndex + 1).find((item) => firstText(item.videoUrl));
    if (!next) {
      setPlaying(false);
      setPlayheadSeconds(timeline.totalDuration);
      return;
    }
    pendingSeekRef.current = { arrayIndex: next.arrayIndex, localSeconds: 0, autoPlay: true };
    setActiveSegmentIndex(next.arrayIndex);
    setPlayheadSeconds(next.startSeconds);
    setPlaying(true);
  }, [activeSegmentIndex, timeline.rows, timeline.totalDuration]);

  const selectAdjacentSegment = useCallback((direction) => {
    const candidates = direction < 0
      ? timeline.rows.slice(0, activeSegmentIndex).reverse()
      : timeline.rows.slice(activeSegmentIndex + 1);
    const target = candidates.find((item) => firstText(item.videoUrl)) || candidates[0];
    if (target) seekTo(target.startSeconds, { autoPlay: false });
  }, [activeSegmentIndex, seekTo, timeline.rows]);

  const togglePlayback = useCallback(() => {
    const video = videoRef.current;
    if (!video || !firstText(activeSegment?.videoUrl)) {
      const firstPlayable = timeline.rows.find((item) => firstText(item.videoUrl));
      if (firstPlayable) seekTo(firstPlayable.startSeconds, { autoPlay: true });
      return;
    }
    if (video.paused) {
      video.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      video.pause();
      setPlaying(false);
    }
  }, [activeSegment?.videoUrl, seekTo, timeline.rows]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.target instanceof HTMLInputElement
        || event.target instanceof HTMLTextAreaElement
        || event.target instanceof HTMLSelectElement) return;
      if (event.code === "Space") {
        event.preventDefault();
        togglePlayback();
      } else if (event.key === "ArrowLeft") {
        seekTo(playheadSeconds - 5, { autoPlay: playing });
      } else if (event.key === "ArrowRight") {
        seekTo(playheadSeconds + 5, { autoPlay: playing });
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [playheadSeconds, playing, seekTo, togglePlayback]);

  const newIssue = useCallback(() => {
    const segment = currentSegmentAt(playheadSeconds);
    setFocusedIssueId(null);
    setDraft(createDraft({
      projectId,
      episodeId,
      seconds: playheadSeconds,
      segmentIndex: segment?.segmentIndex || 1,
    }));
  }, [currentSegmentAt, episodeId, playheadSeconds, projectId]);

  const selectIssue = useCallback((issue) => {
    setFocusedIssueId(issue.id);
    setDraft({
      ...issue,
      referenceImages: Array.isArray(issue.referenceImages) ? issue.referenceImages : [],
    });
    seekTo(Number(issue.timecodeSeconds) || 0, { autoPlay: false });
  }, [seekTo]);

  const patchDraft = useCallback((patch) => {
    setDraft((current) => ({ ...current, ...patch }));
  }, []);

  const saveIssue = useCallback(async () => {
    const problem = firstText(draft?.problem);
    if (!problem) {
      toast.warning("先写下需要修改的问题");
      return;
    }
    const effectiveTimecode = draft?.id
      ? Number(draft.timecodeSeconds) || 0
      : Math.round(playheadSeconds * 10) / 10;
    const segment = currentSegmentAt(effectiveTimecode);
    const payload = {
      ...draft,
      projectId: Number(projectId),
      episodeId: Number(episodeId),
      timecodeSeconds: effectiveTimecode,
      segmentIndex: segment?.segmentIndex || draft.segmentIndex || 1,
      title: firstText(problem.split(/\r?\n/)[0].slice(0, 120), "未命名问题"),
      problem,
      referenceImages: (draft.referenceImages || []).slice(0, 12),
    };
    setSaving(true);
    try {
      const saved = await authenticatedFetch(`${API_HOST}/api/episode-revision-issues`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setIssues((current) => {
        const exists = current.some((item) => Number(item.id) === Number(saved.id));
        return (exists
          ? current.map((item) => Number(item.id) === Number(saved.id) ? saved : item)
          : [...current, saved]
        ).sort((a, b) => Number(a.timecodeSeconds || 0) - Number(b.timecodeSeconds || 0));
      });
      setFocusedIssueId(null);
      setDraft(createDraft({
        projectId,
        episodeId,
        seconds: playheadSeconds,
        segmentIndex: currentSegmentAt(playheadSeconds)?.segmentIndex || 1,
      }));
      window.dispatchEvent(new CustomEvent("episode-revision-issues-change", {
        detail: { projectId: Number(projectId), episodeId: Number(episodeId) },
      }));
      toast.success(draft?.id ? "反馈修改已保存" : "反馈已发布，并同步到小故事板时间轴");
    } catch (requestError) {
      toast.error(requestError?.message || "保存反馈失败", 0);
    } finally {
      setSaving(false);
    }
  }, [currentSegmentAt, draft, episodeId, playheadSeconds, projectId, toast]);

  const updateIssueStatus = useCallback(async (issue, status) => {
    if (!issue?.id || updatingStatusId != null || issue.status === status) return;
    setUpdatingStatusId(issue.id);
    try {
      const saved = await authenticatedFetch(`${API_HOST}/api/episode-revision-issues`, {
        method: "POST",
        body: JSON.stringify({
          ...issue,
          projectId: Number(projectId),
          episodeId: Number(episodeId),
          status,
          referenceImages: Array.isArray(issue.referenceImages) ? issue.referenceImages : [],
        }),
      });
      setIssues((current) => current.map((item) => (
        Number(item.id) === Number(saved.id) ? saved : item
      )));
      setDraft((current) => (
        Number(current?.id) === Number(saved.id)
          ? { ...saved, referenceImages: Array.isArray(saved.referenceImages) ? saved.referenceImages : [] }
          : current
      ));
      window.dispatchEvent(new CustomEvent("episode-revision-issues-change", {
        detail: { projectId: Number(projectId), episodeId: Number(episodeId) },
      }));
      toast.success(`已标记为${QUICK_STATUSES.find((item) => item.id === status)?.name || getStatusName(status)}`);
    } catch (requestError) {
      toast.error(requestError?.message || "反馈状态更新失败", 0);
    } finally {
      setUpdatingStatusId(null);
    }
  }, [episodeId, projectId, toast, updatingStatusId]);

  const deleteIssue = useCallback(async () => {
    if (!draft?.id) {
      newIssue();
      return;
    }
    if (!(await appConfirm(`确定删除「${firstText(draft.title, "当前反馈")}」吗？`))) return;
    setSaving(true);
    try {
      await authenticatedFetch(
        `${API_HOST}/api/episode-revision-issues?id=${draft.id}&projectId=${projectId}&episodeId=${episodeId}`,
        { method: "DELETE" },
      );
      setIssues((current) => current.filter((item) => Number(item.id) !== Number(draft.id)));
      window.dispatchEvent(new CustomEvent("episode-revision-issues-change", {
        detail: { projectId: Number(projectId), episodeId: Number(episodeId) },
      }));
      newIssue();
      toast.success("反馈已删除");
    } catch (requestError) {
      toast.error(requestError?.message || "删除反馈失败", 0);
    } finally {
      setSaving(false);
    }
  }, [draft, episodeId, newIssue, projectId, toast]);

  const uploadReferences = useCallback(async (event) => {
    const files = Array.from(event.target.files || []).filter((file) => file.type.startsWith("image/"));
    event.target.value = "";
    if (!files.length) return;
    const remaining = Math.max(0, 12 - (draft.referenceImages || []).length);
    if (!remaining) {
      toast.warning("每条反馈最多上传 12 张参考图");
      return;
    }
    setUploading(true);
    try {
      const urls = [];
      for (const file of files.slice(0, remaining)) {
        const uploaded = await uploadFileToCos(file);
        const url = firstText(uploaded?.url, uploaded);
        if (url) urls.push(url);
      }
      patchDraft({ referenceImages: [...(draft.referenceImages || []), ...urls] });
    } catch (uploadError) {
      toast.error(uploadError?.message || "参考图上传失败", 0);
    } finally {
      setUploading(false);
    }
  }, [draft.referenceImages, patchDraft, toast]);

  const handleTimelinePointer = useCallback((event) => {
    if (!timelineRef.current || !timeline.rows.length) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const toSeconds = (clientX) => secondsAtPixel(clientX - rect.left);
    seekTo(toSeconds(event.clientX), { autoPlay: false });
    const move = (moveEvent) => seekTo(toSeconds(moveEvent.clientX), { autoPlay: false });
    const cleanup = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", cleanup);
      dragCleanupRef.current = null;
    };
    dragCleanupRef.current?.();
    dragCleanupRef.current = cleanup;
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", cleanup);
  }, [secondsAtPixel, seekTo, timeline.rows.length]);

  const returnToStoryboard = useCallback(() => {
    navigate(firstText(
      location.state?.returnTo,
      `/project/${projectId}/episode/${episodeId}/mini-storyboard`,
    ));
  }, [episodeId, location.state?.returnTo, navigate, projectId]);

  const rulerTicks = useMemo(() => {
    const result = [];
    for (let seconds = 0; seconds <= timeline.totalDuration; seconds += 5) {
      result.push({ seconds, left: pixelAtSeconds(seconds), major: seconds % 15 === 0 });
    }
    return result;
  }, [pixelAtSeconds, timeline.totalDuration]);

  if (loading) {
    return (
      <div className="review-page-state">
        <Loader2 size={28} className="animate-spin" />
        <span>正在装载审片时间线...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="review-page-state">
        <MessageSquareWarning size={30} />
        <strong>审片页面暂时无法打开</strong>
        <span>{error}</span>
        <div className="review-state-actions">
          <button type="button" onClick={returnToStoryboard}><ArrowLeft size={16} />返回小故事板</button>
          <button type="button" onClick={loadPageData}>重试</button>
        </div>
      </div>
    );
  }

  return (
    <div className="episode-review-page">
      <header className="review-header">
        <div className="review-header-leading">
          <button type="button" className="review-icon-button" onClick={returnToStoryboard} title="返回小故事板">
            <ArrowLeft size={18} />
          </button>
          <div className="review-heading">
            <div className="review-title-row">
              <h1>审片与意见反馈</h1>
              <span>{openIssueCount} 条待处理</span>
            </div>
            <p>{firstText(episode?.name, episode?.title, episode?.episodeName, `第 ${episodeId} 集`)} · 连续播放当前小故事板已选视频</p>
          </div>
        </div>
        <div className="review-header-actions">
          <span className="review-header-time"><Clock3 size={15} />{formatTime(playheadSeconds, true)} / {formatTime(timeline.totalDuration)}</span>
        </div>
      </header>

      <main className="review-workspace">
        <section className="review-player-panel">
          <div className="review-panel-label">
            <div>
              <strong>连续审片</strong>
              <span>片段 {String(activeSegment?.segmentIndex || 0).padStart(2, "0")} · {firstText(activeSegment?.title, "当前片段")}</span>
            </div>
            <div className="review-segment-nav">
              <button type="button" onClick={() => selectAdjacentSegment(-1)} title="上一个片段">
                <ChevronLeft size={17} />
              </button>
              <span>{activeSegmentIndex + 1} / {timeline.rows.length}</span>
              <button type="button" onClick={() => selectAdjacentSegment(1)} title="下一个片段">
                <ChevronRight size={17} />
              </button>
            </div>
          </div>

          <div className="review-video-stage">
            {firstText(activeSegment?.videoUrl) ? (
              <>
                <video
                  key={activeSegment?.id || activeSegment?.segmentIndex}
                  ref={videoRef}
                  src={activeSegment.videoUrl}
                  playsInline
                  preload="auto"
                  onLoadedMetadata={handleLoadedMetadata}
                  onTimeUpdate={handleTimeUpdate}
                  onPlay={() => setPlaying(true)}
                  onPause={() => setPlaying(false)}
                  onEnded={playNext}
                />
                {nextPlayableSegment?.videoUrl && (
                  <video ref={nextVideoRef} src={nextPlayableSegment.videoUrl} preload="auto" className="review-preload-video" />
                )}
                <button type="button" className="review-stage-play" onClick={togglePlayback} aria-label={playing ? "暂停" : "播放"}>
                  {playing ? <Pause size={28} fill="currentColor" /> : <Play size={28} fill="currentColor" />}
                </button>
                <div className="review-player-controls">
                  <button type="button" onClick={() => seekTo(playheadSeconds - 5, { autoPlay: playing })} title="后退 5 秒"><SkipBack size={17} /></button>
                  <button type="button" onClick={togglePlayback} title={playing ? "暂停" : "播放"}>
                    {playing ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" />}
                  </button>
                  <button type="button" onClick={() => seekTo(playheadSeconds + 5, { autoPlay: playing })} title="前进 5 秒"><SkipForward size={17} /></button>
                  <span>{formatTime(playheadSeconds)} / {formatTime(timeline.totalDuration)}</span>
                  <div className="review-progress">
                    <div style={{ width: `${timeline.totalDuration ? playheadSeconds / timeline.totalDuration * 100 : 0}%` }} />
                  </div>
                  <span>分镜组 {String(activeSegment?.segmentIndex || 0).padStart(2, "0")}</span>
                </div>
              </>
            ) : (
              <div className="review-video-empty">
                <Play size={34} />
                <strong>当前片段还没有可审视频</strong>
                <span>可以在时间线上选择其他已生成片段。</span>
              </div>
            )}
          </div>
        </section>

        <aside className="review-feedback-panel">
          <div className="review-feedback-toolbar">
            <div>
              <strong>问题反馈</strong>
              <span>{filteredIssues.length} 条结果</span>
            </div>
          </div>

          <div className="review-filters">
            <AppSelect value={filterPriority} onChange={(event) => setFilterPriority(event.target.value)} aria-label="按优先级筛选">
              <option value="all">全部优先级</option>
              {PRIORITIES.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </AppSelect>
            <AppSelect value={filterType} onChange={(event) => setFilterType(event.target.value)} aria-label="按类型筛选">
              <option value="all">全部类型</option>
              {ISSUE_TYPES.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </AppSelect>
            <AppSelect value={filterStatus} onChange={(event) => setFilterStatus(event.target.value)} aria-label="按状态筛选">
              <option value="all">全部状态</option>
              <option value="active">待处理与处理中</option>
              {ISSUE_STATUSES.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </AppSelect>
          </div>

          <div className="review-issue-list">
            {!filteredIssues.length ? (
              <div className="review-issue-empty">
                <MessageSquareWarning size={24} />
                <strong>这个筛选下还没有反馈</strong>
                <span>播放到问题发生处，直接在下方发布第一条反馈。</span>
              </div>
            ) : filteredIssues.map((issue) => {
              const priority = getPriority(issue.priority);
              const selected = Number(focusedIssueId) === Number(issue.id);
              return (
                <div
                  key={issue.id}
                  className={`review-issue-card${selected ? " is-selected" : ""}`}
                >
                  <button type="button" className="review-issue-main" onClick={() => selectIssue(issue)}>
                    <span className="review-issue-priority" style={{ color: priority.color, borderColor: priority.color }}>{priority.id}</span>
                    <span className="review-issue-copy">
                      <strong>{firstText(issue.title, issue.problem, "未命名问题")}</strong>
                      <span>{getTypeName(issue.type)} · {getStatusName(issue.status)} · 分镜组 {String(issue.segmentIndex || 1).padStart(2, "0")}</span>
                    </span>
                    <span className="review-issue-time">{formatTime(issue.timecodeSeconds, true)}</span>
                  </button>
                  <div className="review-issue-statuses" aria-label="快速更新反馈状态">
                    {QUICK_STATUSES.map((status) => {
                      const active = issue.status === status.id;
                      const updating = updatingStatusId != null;
                      return (
                        <button
                          type="button"
                          key={status.id}
                          className={active ? "is-active" : ""}
                          disabled={updating}
                          aria-pressed={active}
                          onClick={() => updateIssueStatus(issue, status.id)}
                        >
                          <span className="review-status-check">
                            {active && <Check size={11} strokeWidth={3} />}
                          </span>
                          {status.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="review-composer">
            <div className="review-composer-meta">
              <span className="review-time-button" title={draft.id ? "正在修改这条反馈的原时间点" : "时间随当前视频播放位置自动更新"}>
                <Clock3 size={14} />
                {formatTime(draft.id ? draft.timecodeSeconds : playheadSeconds, true)}
              </span>
              <AppSelect value={draft.priority || "P2"} onChange={(event) => patchDraft({ priority: event.target.value })} aria-label="反馈优先级">
                {PRIORITIES.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </AppSelect>
              <AppSelect value={draft.type || "story"} onChange={(event) => patchDraft({ type: event.target.value })} aria-label="反馈类型">
                {ISSUE_TYPES.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </AppSelect>
              <AppSelect value={draft.status || "open"} onChange={(event) => patchDraft({ status: event.target.value })} aria-label="反馈状态">
                {ISSUE_STATUSES.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </AppSelect>
            </div>
            <textarea
              value={draft.problem || ""}
              onChange={(event) => patchDraft({ problem: event.target.value })}
              placeholder="写下这个时间点的问题和修改意见。保存后会同步到小故事板粗剪时间轴。"
            />
            {(draft.referenceImages || []).length > 0 && (
              <div className="review-reference-list">
                {draft.referenceImages.map((url, index) => (
                  <div key={`${url}-${index}`} className="review-reference-thumb">
                    <img src={url} alt={`参考图 ${index + 1}`} />
                    <button
                      type="button"
                      onClick={() => patchDraft({
                        referenceImages: draft.referenceImages.filter((_, currentIndex) => currentIndex !== index),
                      })}
                      title="移除参考图"
                    >
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="review-composer-actions">
              <input ref={uploadInputRef} type="file" accept="image/*" multiple hidden onChange={uploadReferences} />
              <button type="button" onClick={() => uploadInputRef.current?.click()} disabled={uploading}>
                {uploading ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />}
                参考图
              </button>
              {draft.id && (
                <button type="button" className="is-danger" onClick={deleteIssue} disabled={saving}>
                  <Trash2 size={15} />删除
                </button>
              )}
              <span />
              <button type="button" className="review-primary-button" onClick={saveIssue} disabled={saving || !firstText(draft.problem)}>
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                {draft.id ? "保存修改" : "发布反馈"}
              </button>
            </div>
          </div>
        </aside>
      </main>

      <section className="review-timeline-panel">
        <div className="review-timeline-heading">
          <div>
            <strong>审片时间轴</strong>
            <span>{timeline.rows.length} 个片段 · {timeline.rows.filter((item) => firstText(item.videoUrl)).length} 个已生成 · 点击或拖动指针定位</span>
          </div>
          <div className="review-timeline-legend">
            {PRIORITIES.map((item) => <span key={item.id}><i style={{ backgroundColor: item.color }} />{item.id}</span>)}
          </div>
        </div>
        <div className="review-timeline-scroll">
          <div
            ref={timelineRef}
            className="review-timeline-content"
            style={{ width: Math.max(timeline.width, 900) }}
          >
            <div className="review-ruler">
              {rulerTicks.map((tick) => (
                <span
                  key={tick.seconds}
                  className={tick.major ? "is-major" : ""}
                  style={{ left: tick.left }}
                >
                  <i />
                  {tick.major && <em>{formatTime(tick.seconds)}</em>}
                </span>
              ))}
            </div>
            <div className="review-issue-lane">
              {issues.map((issue) => {
                const priority = getPriority(issue.priority);
                return (
                  <button
                    type="button"
                    key={issue.id}
                    className="review-timeline-issue"
                    style={{ left: pixelAtSeconds(issue.timecodeSeconds), color: priority.color }}
                    onClick={(event) => {
                      event.stopPropagation();
                      selectIssue(issue);
                    }}
                    title={`${formatTime(issue.timecodeSeconds, true)} ${firstText(issue.title, issue.problem)}`}
                  >
                    <MessageSquareWarning size={14} fill="currentColor" />
                  </button>
                );
              })}
            </div>
            <div className="review-clip-track">
              {timeline.rows.map((segment) => (
                <button
                  type="button"
                  key={segment.id || segment.segmentIndex}
                  className={`review-clip${segment.arrayIndex === activeSegmentIndex ? " is-active" : ""}${firstText(segment.videoUrl) ? "" : " is-empty"}`}
                  style={{ left: segment.left, width: segment.width }}
                  onClick={(event) => {
                    event.stopPropagation();
                    const rect = event.currentTarget.getBoundingClientRect();
                    const ratio = clamp((event.clientX - rect.left) / Math.max(1, rect.width), 0, 1);
                    seekTo(segment.startSeconds + ratio * segment.durationSeconds, { autoPlay: false });
                  }}
                >
                  {firstText(segment.thumbnailUrl, segment.coverUrl) ? (
                    <img src={firstText(segment.thumbnailUrl, segment.coverUrl)} alt="" />
                  ) : (
                    <span className={`review-clip-placeholder${firstText(segment.videoUrl) ? "" : " is-missing"}`}>
                      <Film size={18} />
                    </span>
                  )}
                  <span className="review-clip-number">{String(segment.segmentIndex || 1).padStart(2, "0")}</span>
                  <span className="review-clip-duration">{formatTime(segment.startSeconds)} · {segment.durationSeconds}s</span>
                </button>
              ))}
            </div>
            <div className="review-waveform" aria-hidden="true">
              {Array.from({ length: Math.max(60, Math.round(timeline.width / 12)) }).map((_, index) => (
                <i key={index} style={{ height: `${20 + ((index * 17) % 44)}%` }} />
              ))}
            </div>
            <div
              role="slider"
              aria-label="审片时间指针"
              aria-valuemin={0}
              aria-valuemax={Math.round(timeline.totalDuration)}
              aria-valuenow={Math.round(playheadSeconds)}
              className="review-playhead"
              style={{ left: pixelAtSeconds(playheadSeconds) }}
              onPointerDown={(event) => {
                event.stopPropagation();
                handleTimelinePointer(event);
              }}
            >
              <span />
            </div>
            <button type="button" className="review-timeline-hitarea" onPointerDown={handleTimelinePointer} aria-label="点击时间轴定位" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default EpisodeReviewFeedbackPage;
